/**
 * The projects behind the two Statistiques boards.
 *
 * Both are read once, at prerender time, from the public `/search/projects` endpoint, and the few
 * rows that survive are embedded in the page as JSON. There is NO client refetch, unlike the
 * platform stats: one all-time query alone answers ~440 KB, because the search response carries
 * every project's eight translations, images and owner, and that is a bad trade for rankings that
 * barely move. The boards are therefore as fresh as the last deploy.
 *
 * That is also why the Top 10's per-category lists are prerendered rather than fetched when the
 * filter changes: nineteen embedded boards cost ~55 KB of JSON once, where fetching one on each
 * selection would cost ~440 KB every time.
 *
 * The boards do not share a source:
 *  - `topAllTime` and `topByCategory` mix live Ulule projects with the frozen `kkbbProjects.json`,
 *    ranked by amount;
 *  - `popularNow` is Ulule-only — the best-backed of the currently-funding collections the
 *    platform's popularity sort surfaces. KKBB never appears there: its projects have all ended.
 */
import { ULULE_CATEGORIES } from './categories'
import { KKBB, kkbbImageUrl, ululeCategoryId, type KkbbProject } from './kkbbProjects'

/** A project as the boards render it, whichever platform collected it. */
export type Collection = {
  name: string
  /** Public project page; the whole row or card links to it. */
  url: string
  /**
   * Total collected. Treated as euros: the endpoint reports each project's own currency and we do
   * not convert, so a non-EUR project would rank against euro amounts as if it were one. Every
   * project near the top is EUR today, which is why this is acceptable for now.
   */
  amountEur: number
  category: string
  /** Distinct contributors. Only `popularNow` shows it, and it is what orders that board. */
  backersCount: number
  /** Ulule project id. Absent on KissKissBankBank rows, which have no Ulule resource. */
  id?: number
  /**
   * A project that pre-sells products rather than collecting towards a goal. Its revenue is sales,
   * not a collection, so the all-time board shows `preSalesCount` in place of `amountEur` — it
   * still ranks on the amount, which is the comparable figure.
   */
  isPresale: boolean
  /** Items pre-ordered. Only meaningful, and only shown, on a presale. */
  preSalesCount: number
  /** This presale counts "contributions" rather than "préventes" — see `fetchContributionWording`. */
  usesContributionWording: boolean
  /** Thumbnail or cover. Absent when the project has no image, which the row falls back on. */
  imageUrl?: string
  /**
   * Collected on KissKissBankBank rather than Ulule. Not surfaced per row today — the all-time
   * board says so once, in its lede — but carried so a row could mark itself without a refetch.
   */
  fromKkbb?: boolean
}

/** One option of the Top 10 category filter, with the board it selects. */
export type CategoryBoard = {
  /** Category slug: the `<select>` value and the `?cat=` parameter. */
  slug: string
  /** Option label. */
  label: string
  entries: Collection[]
}

export type Collections = {
  /** Empty when the Ulule side could not be read — see `buildTopAllTime`. */
  topAllTime: Collection[]
  /** In the site's category order. A category whose search failed is left out entirely. */
  topByCategory: CategoryBoard[]
  popularNow: Collection[]
}

export const COLLECTIONS_DATA_ID = 'ulule-collections-data'
export const EMPTY_COLLECTIONS: Collections = { topAllTime: [], topByCategory: [], popularNow: [] }

const SEARCH_URL = 'https://api.ulule.com/v1/search/projects'
const FETCH_TIMEOUT_MS = 8_000

const TOP_QUERY = 'sort:amount status:success'

/**
 * The pool the Populaires cards rank, and the reason they rank a pool at all: the endpoint sorts
 * by `amount`, `popular`, `new` or `ending-soon` and 422s on anything else, so nothing answers
 * "most contributors" directly. Popularity is the right pool to draw from because contributor
 * counts are cumulative — rank a broad one and a long campaign wins on age alone, pinning the
 * same extended collections in place for months.
 */
const POPULAR_QUERY = 'sort:popular status:currently'

/**
 * Deeper than the ten rows we show, because `sort:amount` only roughly orders the results — we
 * re-rank locally, so the extra results are what makes the top ten actually the top ten. Costs
 * nothing a reader pays: this runs at prerender, and only the ten rows are embedded.
 */
const TOP_FETCH_LIMIT = 40
const TOP_SIZE = 10

/**
 * The section's age dial rather than a fetch detail, for the reason above: at forty the cards
 * reached a 113-day collection in extra time and sat it at card two. Four times the board leaves
 * "best-backed" something to choose between while every candidate is still in the popular head.
 */
const POPULAR_FETCH_LIMIT = 20
const POPULAR_SIZE = 5

/**
 * How the eighteen category searches are paced. Each answers ~440 KB, so they go four at a time
 * rather than putting ~8 MB in flight at once, and a failure gets one retry after a pause long
 * enough for a rate-limit window to move on.
 */
const SEARCH_CONCURRENCY = 4
const SEARCH_ATTEMPTS = 2
const RETRY_DELAY_MS = 1_000

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

type SearchProject = {
  absolute_url?: unknown
  id?: unknown
  amount_raised?: unknown
  is_featured?: unknown
  nb_products_sold?: unknown
  type?: unknown
  main_image?: unknown
  main_tag?: unknown
  name_fr?: unknown
  supporters_count?: unknown
}

/**
 * The crops both boards read out of `main_image`, which is a map of size key to URL, best first.
 *
 * `258x145` is the smallest crop that still covers the leaderboard's 112x80 thumb at 2x, and the
 * largest the Populaires cards can afford. Their covers run to 296px wide, so `640x360` would
 * serve them sharply on a 2x screen — but the resizer keeps every frame of an animated GIF, and
 * the five cards on the live board came to 5 MB at that crop against 1 MB at this one. The cards
 * take a soft cover instead, and a project on both boards is fetched once rather than twice.
 *
 * `230x126` is the fallback because the API does not build every crop for every project — Canard
 * PC TV and BACKSEAT carry only that one and the untouched original.
 */
const IMAGE_SIZES = ['258x145', '230x126'] as const

function parseImageUrl(source: unknown): string | undefined {
  if (typeof source !== 'object' || source === null) return undefined

  const image = source as Record<string, unknown>

  return IMAGE_SIZES.map((size) => image[size]).find((url): url is string => typeof url === 'string' && url !== '')
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

/**
 * Editorially promoted projects are returned first whatever the sort, so they break an amount
 * ranking — the featured project we saw led the list at 14 103 € over one at 4 634 968 €.
 */
function isFeatured(entry: unknown): boolean {
  return typeof entry === 'object' && entry !== null && (entry as SearchProject).is_featured === true
}

/** `TYPE_CHOICES.PRESALE` in the Django constants — a project that pre-sells products. */
const PRESALE_TYPE = 1

/** Drops anything missing a name, a link or an amount; those cannot make a meaningful row. */
function parseCollection(entry: unknown): Collection | null {
  if (typeof entry !== 'object' || entry === null) return null

  const project = entry as SearchProject
  const tag = typeof project.main_tag === 'object' && project.main_tag !== null ? project.main_tag : null

  const name = typeof project.name_fr === 'string' ? project.name_fr.trim() : ''
  const url = typeof project.absolute_url === 'string' ? project.absolute_url : ''
  const category =
    typeof (tag as { name_fr?: unknown } | null)?.name_fr === 'string' ? (tag as { name_fr: string }).name_fr : ''

  if (name === '' || url === '' || !isPositiveNumber(project.amount_raised)) return null

  return {
    name,
    url,
    category,
    amountEur: project.amount_raised,
    ...(isPositiveNumber(project.id) ? { id: project.id } : {}),
    isPresale: project.type === PRESALE_TYPE,
    usesContributionWording: false,
    preSalesCount: isPositiveNumber(project.nb_products_sold) ? Math.round(project.nb_products_sold) : 0,
    imageUrl: parseImageUrl(project.main_image),
    backersCount: isPositiveNumber(project.supporters_count) ? Math.round(project.supporters_count) : 0,
  }
}

export function parseSearchResponse(source: unknown, { excludeFeatured }: { excludeFeatured: boolean }): Collection[] {
  if (typeof source !== 'object' || source === null) return []

  const projects = (source as { projects?: unknown }).projects
  if (!Array.isArray(projects)) return []

  return projects
    .filter((entry) => !excludeFeatured || !isFeatured(entry))
    .flatMap((entry) => {
      const collection = parseCollection(entry)
      return collection === null ? [] : [collection]
    })
}

/** One KKBB row, in the shape the boards rank. */
function toKkbbCollection(project: KkbbProject): Collection {
  return {
    name: project.name,
    url: project.url,
    amountEur: project.amountEur,
    // KKBB's own label, not the Ulule category it maps to: it is the only thing on the row that
    // says where the project was collected.
    category: project.categories[0] ?? '',
    // KKBB had no presale type: every project there collected towards an amount.
    isPresale: false,
    preSalesCount: 0,
    usesContributionWording: false,
    imageUrl: kkbbImageUrl(project.image),
    backersCount: project.backersCount,
    fromKkbb: true,
  }
}

const KKBB_COLLECTIONS: Collection[] = KKBB.projects.map(toKkbbCollection)

/** The same rows, bucketed by the Ulule category their primary KKBB label maps to. */
const KKBB_BY_CATEGORY_ID: ReadonlyMap<number, Collection[]> = KKBB.projects.reduce((buckets, project) => {
  const categoryId = ululeCategoryId(project)
  if (categoryId !== undefined) {
    buckets.set(categoryId, [...(buckets.get(categoryId) ?? []), toKkbbCollection(project)])
  }

  return buckets
}, new Map<number, Collection[]>())

/**
 * Ulule + KissKissBankBank, ranked by amount.
 *
 * Returns nothing when the Ulule side is missing: KKBB's projects are always available, so the
 * board would otherwise quietly become a KKBB-only list under a heading that claims to cover both.
 */
export function buildTopAllTime(
  ululeCollections: Collection[] | null,
  kkbbCollections: Collection[] = KKBB_COLLECTIONS,
): Collection[] {
  if (ululeCollections === null || ululeCollections.length === 0) return []

  return [...ululeCollections, ...kkbbCollections]
    .sort((one, other) => other.amountEur - one.amountEur)
    .slice(0, TOP_SIZE)
}

const PROJECT_URL = 'https://api.ulule.com/v1/projects'

/**
 * Per-project setting deciding whether a presale counts "contributions" rather than "préventes".
 *
 * This is the rule the public project page follows — see `models.project.presale.countNoun`, which
 * reads the same `custom_config` flag — so a project whose owner asked for that wording gets it
 * here too, with no list of project ids anywhere. We cannot import that model: it pulls in
 * `@owl-nest/models`, which reads UFE config at module-eval time and so cannot be evaluated in the
 * standalone prerender. The flag key is therefore duplicated, and the two must stay in step.
 *
 * TODO: extract the key, and ideally `config.flag` itself, into a dependency-free module both can
 * import, so the two cannot drift. Worth doing as soon as a second `custom_config` setting shows up
 * here — the duplication is tolerable for one flag, not for a habit.
 */
const CONTRIBUTION_WORDING_FLAG = 'use_contribution_wording_for_presales'

/**
 * Read that flag for one project. Only `/v1/projects/<id>` carries `custom_config` — the search
 * payload does not — so this costs one request per presale row the boards actually show.
 *
 * Falls back to false, i.e. "préventes", which is the same default the project page applies.
 */
async function fetchContributionWording(id: number, pageId: string): Promise<boolean> {
  try {
    const response = await fetch(`${PROJECT_URL}/${id}`, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
    if (!response.ok) {
      console.warn(`${pageId} prerender: project ${id} returned HTTP ${response.status}; defaulting its wording`)
      return false
    }

    const project: unknown = await response.json()
    const customConfig = (project as { custom_config?: Record<string, unknown> | null }).custom_config

    return customConfig?.[CONTRIBUTION_WORDING_FLAG] === true
  } catch {
    console.warn(`${pageId} prerender: project ${id} could not be reached; defaulting its wording`)
    return false
  }
}

/**
 * In-flight and settled wording lookups, keyed by project id. A presale that tops several boards —
 * the all-time list and its own category, say — is then read once rather than once per board.
 */
type WordingCache = Map<number, Promise<boolean>>

/**
 * Resolves the wording for the presale rows a board displays, leaving every other row untouched.
 *
 * Batched like the searches, and for the same endpoint's sake: four category boards resolve at
 * once, so an unbounded pass here could put dozens of project lookups in flight alongside them.
 * Rows that are not presales settle immediately, so the batching costs them nothing.
 */
async function withPresaleWording(
  collections: Collection[],
  pageId: string,
  cache: WordingCache,
): Promise<Collection[]> {
  return mapInBatches(collections, SEARCH_CONCURRENCY, async (collection) => {
    const id = collection.id
    if (!collection.isPresale || id === undefined) return collection

    const wording = cache.get(id) ?? fetchContributionWording(id, pageId)
    cache.set(id, wording)

    return { ...collection, usesContributionWording: await wording }
  })
}

/** Runs `task` over `items` a few at a time, so a long list cannot flood the endpoint. */
async function mapInBatches<T, R>(items: readonly T[], size: number, task: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = []

  for (let start = 0; start < items.length; start += size) {
    results.push(...(await Promise.all(items.slice(start, start + size).map(task))))
  }

  return results
}

/** A rejected query answers the same way twice; only congestion and outages are worth retrying. */
function isWorthRetrying(status: number): boolean {
  return status === 429 || status >= 500
}

/**
 * Read one search, retrying once.
 *
 * The prerender fires twenty of these back to back, which is what a rate limit answers, and one
 * dropped response costs a whole board — or, for a category, removes it from the filter until the
 * next deploy. A retry is close to free at build time and that outcome is not.
 */
async function search(query: string, limit: number, pageId: string): Promise<unknown | null> {
  const url = `${SEARCH_URL}?q=${encodeURIComponent(query)}&limit=${limit}`

  for (let attempt = 1; attempt <= SEARCH_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
      if (response.ok) return await response.json()

      console.warn(`${pageId} prerender: "${query}" returned HTTP ${response.status}; that board ships empty`)
      if (!isWorthRetrying(response.status)) return null
    } catch {
      console.warn(`${pageId} prerender: "${query}" could not be reached on attempt ${attempt}`)
    }

    if (attempt < SEARCH_ATTEMPTS) await wait(RETRY_DELAY_MS)
  }

  console.warn(`${pageId} prerender: "${query}" failed every attempt; that board ships empty`)
  return null
}

/**
 * One all-time board per category, in the site's category order.
 *
 * A category whose search failed, or that came back with nothing, is dropped rather than offered as
 * an option that selects an empty — or KKBB-only — list. A category holding fewer than ten projects
 * simply shows the ones it has.
 */
async function fetchCategoryBoards(pageId: string, wording: WordingCache): Promise<CategoryBoard[]> {
  const boards = await mapInBatches(ULULE_CATEGORIES, SEARCH_CONCURRENCY, async (category) => {
    const response = await search(`${TOP_QUERY} tag_id:${category.id}`, TOP_FETCH_LIMIT, pageId)
    if (response === null) return []

    const entries = buildTopAllTime(
      parseSearchResponse(response, { excludeFeatured: true }),
      KKBB_BY_CATEGORY_ID.get(category.id) ?? [],
    )
    if (entries.length === 0) return []

    return [{ slug: category.slug, label: category.label, entries: await withPresaleWording(entries, pageId, wording) }]
  })

  return boards.flat()
}

/**
 * Read both boards at prerender time. Never throws: a board that cannot be read comes back empty
 * and its section is dropped, rather than failing the build or publishing a half-list.
 */
export async function fetchCollections(pageId: string): Promise<Collections> {
  const wording: WordingCache = new Map()

  const [top, popular] = await Promise.all([
    search(TOP_QUERY, TOP_FETCH_LIMIT, pageId),
    search(POPULAR_QUERY, POPULAR_FETCH_LIMIT, pageId),
  ])

  // Only the all-time boards name what a presale sold, so only their rows need the wording flag.
  const topAllTime = await withPresaleWording(
    buildTopAllTime(top === null ? null : parseSearchResponse(top, { excludeFeatured: true })),
    pageId,
    wording,
  )

  return {
    topAllTime,
    // An empty all-time board means the Ulule search is unreachable, so there is no point asking it
    // eighteen more times — and the filter would have nothing to offer either way.
    topByCategory: topAllTime.length === 0 ? [] : await fetchCategoryBoards(pageId, wording),
    // Featured projects are kept. They are returned first whatever the sort, which is why the
    // all-time board drops them — but this pool is an editorial one, where a promotion belongs,
    // and re-ranking it by contributors means a promoted project cannot jump the order anyway.
    popularNow:
      popular === null
        ? []
        : parseSearchResponse(popular, { excludeFeatured: false })
            .sort((one, other) => other.backersCount - one.backersCount)
            .slice(0, POPULAR_SIZE),
  }
}

/** Embed for hydration. `<` is escaped so a project name can never close the script tag. */
export function renderCollectionsScript(collections: Collections): string {
  const json = JSON.stringify(collections).replace(/</g, '\\u003c')
  return `<script id="${COLLECTIONS_DATA_ID}" type="application/json">${json}</script>`
}

function isCollection(value: unknown): value is Collection {
  if (typeof value !== 'object' || value === null) return false

  const collection = value as Record<string, unknown>
  return (
    typeof collection.name === 'string' &&
    typeof collection.url === 'string' &&
    typeof collection.category === 'string' &&
    typeof collection.isPresale === 'boolean' &&
    // Checked because the cards both rank on it and print it: a row without one sorts against NaN
    // and renders "NaN contributeur·ices".
    isNonNegativeNumber(collection.backersCount) &&
    isPositiveNumber(collection.amountEur)
  )
}

function isCategoryBoard(value: unknown): value is CategoryBoard {
  if (typeof value !== 'object' || value === null) return false

  const board = value as Record<string, unknown>
  return (
    typeof board.slug === 'string' &&
    typeof board.label === 'string' &&
    Array.isArray(board.entries) &&
    board.entries.every(isCollection)
  )
}

export function isCollections(value: unknown): value is Collections {
  if (typeof value !== 'object' || value === null) return false

  const collections = value as Record<string, unknown>
  return (
    Array.isArray(collections.topAllTime) &&
    collections.topAllTime.every(isCollection) &&
    Array.isArray(collections.topByCategory) &&
    collections.topByCategory.every(isCategoryBoard) &&
    Array.isArray(collections.popularNow) &&
    collections.popularNow.every(isCollection)
  )
}

/** Read the build-time boards back on the client. Returns empty boards when absent or malformed. */
export function readPrerenderedCollections(): Collections {
  const data = document.getElementById(COLLECTIONS_DATA_ID)?.textContent
  if (data === undefined || data === null) return EMPTY_COLLECTIONS

  try {
    const collections: unknown = JSON.parse(data)
    return isCollections(collections) ? collections : EMPTY_COLLECTIONS
  } catch {
    return EMPTY_COLLECTIONS
  }
}
